import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api';

export const useAcademicData = (tenantId, token, initialSemesterId = null) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [terms, setTerms] = useState([]);
  
  // Track if initial semester has been set to prevent overriding
  const initialSemesterSetRef = useRef(false);

  // 1. Fetch academic years
  const fetchAcademicYears = useCallback(async () => {
    if (!tenantId) return;
    try {
      const response = await api.get(
        `/api/academic-year/all-active?tenantId=${tenantId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      let yearsCandidate = response?.data?.academicYears
        || response?.data?.activeAcademicYears
        || response?.data?.academicYear
        || response?.data
        || [];

      if (yearsCandidate && !Array.isArray(yearsCandidate) && typeof yearsCandidate === 'object') {
        yearsCandidate = [yearsCandidate];
      }

      const years = Array.isArray(yearsCandidate) ? yearsCandidate : [];
      setAcademicYears(years);

      if (years.length > 0 && !selectedAcademicYear) {
        const pick = years[0]._id || years[0].id;
        setSelectedAcademicYear(pick);
      }
    } catch (err) {
      console.error("Error fetching academic years:", err);
      setAcademicYears([]);
    }
  }, [tenantId, token, selectedAcademicYear]);

  // 2. Fetch semesters for selected academic year
  const fetchSemesters = useCallback(async () => {
    if (!selectedAcademicYear) return;
    try {
      const response = await api.get(
        `/api/classManager/semesters/${selectedAcademicYear}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const fetchedSemesters = response.data.semesters || [];
      setSemesters(fetchedSemesters);
      setIsSemesterBased(true);
      
      if (fetchedSemesters.length > 0 && !selectedSemester) {
        if (initialSemesterId && !initialSemesterSetRef.current) {
          const semesterExists = fetchedSemesters.some(s => s._id === initialSemesterId);
          if (semesterExists) {
            setSelectedSemester(initialSemesterId);
            initialSemesterSetRef.current = true;
            return;
          }
        }
        const pick = fetchedSemesters[0]._id || fetchedSemesters[0].id;
        setSelectedSemester(pick);
      }
    } catch (err) {
      setSemesters([]);
      setIsSemesterBased(false);
    }
  }, [selectedAcademicYear, token, selectedSemester, initialSemesterId]);

  // 3. Fetch terms (for non-semester based years)
  const fetchTerms = useCallback(async () => {
    if (!selectedAcademicYear || isSemesterBased) return;
    try {
      const response = await api.get(
        `/api/academic-year/${selectedAcademicYear}/terms?tenantId=${tenantId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      setTerms(response.data.terms || []);
    } catch (err) {
      setTerms([]);
    }
  }, [selectedAcademicYear, isSemesterBased, tenantId, token]);

  // 4. Fetch classes 
  const fetchClasses = useCallback(async () => {
    if (!token || (!selectedAcademicYear && !selectedSemester)) return;
    try {
      let endpoint = '';
      if (isSemesterBased && selectedSemester) {
        endpoint = `/api/classManager/classes-for-semester/${selectedSemester}`;
      } else if (!isSemesterBased && selectedAcademicYear) {
        endpoint = `/api/classManager/classes-for-academic-year/${selectedAcademicYear}`;
      }

      if (endpoint) {
        const response = await api.get(endpoint, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const classList = response.data.classes || [];
        setClasses(classList);
        
        // Auto-select first class if none selected
        if (classList.length > 0 && !selectedClassId) {
          const pick = classList[0]._id || classList[0].id;
          setSelectedClassId(pick);
        }
      }
    } catch (err) {
      setClasses([]);
    }
  }, [isSemesterBased, selectedSemester, selectedAcademicYear, token, selectedClassId]);

  // Effect: Handle Academic Year changes
  useEffect(() => {
    if (!selectedAcademicYear) return;
    const yearObj = academicYears.find(y => y._id === selectedAcademicYear || y.id === selectedAcademicYear);
    
    if (yearObj?.isSemesterBased) {
      fetchSemesters();
    } else {
      setIsSemesterBased(false);
      setSemesters([]);
      setSelectedSemester('');
      fetchClasses();
      fetchTerms();
    }
  }, [selectedAcademicYear, academicYears, fetchSemesters, fetchClasses, fetchTerms]);

  // Effect: Handle Semester changes
  useEffect(() => {
    if (isSemesterBased && selectedSemester) {
      fetchClasses();
    }
  }, [isSemesterBased, selectedSemester, fetchClasses]);

  // Effect: Initial Mount
  useEffect(() => {
    fetchAcademicYears();
  }, [fetchAcademicYears]);

  // Effect: Reset ref if initialSemesterId changes to allow re-initialization
  useEffect(() => {
    if (initialSemesterId) {
      initialSemesterSetRef.current = false;
    }
  }, [initialSemesterId]);

  return {
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
    isSemesterBased,
    semesters,
    selectedSemester,
    setSelectedSemester,
    classes,
    selectedClassId,
    setSelectedClassId,
    terms,
    fetchTerms,
    fetchAcademicData: fetchAcademicYears
  };
};